import { FileText, RotateCcw, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import CircleProgress from "./circle-progress"

import type { AgentFile } from "~/hooks/useAgentFileUpload"

// Square-icon background by file type (matches the accountOS-v2 design:
// docx blue, xlsx green; others get sensible file-type colours).
const EXT_ICON_BG: Record<string, string> = {
  docx: "#2B6CC4",
  doc: "#2B6CC4",
  xlsx: "#1E8E4E",
  xls: "#1E8E4E",
  csv: "#1E8E4E",
  pdf: "#D7443E",
  pptx: "#C55A11",
  ppt: "#C55A11"
}

// All attachment chips render as ONE uniform fixed-width card: square icon +
// name + a tag-and-status subline + remove. The tag reads TEMPLATE (the
// fill-template pick), LIBRARY (Knowledge Library) or MY FILES (default) so
// the row is self-describing and every chip is the same size.
export default function FileChip({
  file,
  disabled,
  onRemove,
  onRetry,
  template
}: {
  file: AgentFile
  disabled?: boolean
  onRemove: (localId: string) => void
  onRetry?: (localId: string) => void
  /** Marks this chip as the fill TEMPLATE → purple "TEMPLATE" tag. */
  template?: boolean
}) {
  const { t } = useTranslation()
  const ext = (file.name?.split(".").pop() ?? "").toLowerCase()
  const iconBg = EXT_ICON_BG[ext] ?? "#64748B"
  const failed = file.phase === "FAILED"

  const tag = template
    ? {
        label: t("ask_os.label_template_tag"),
        cls: "bg-primary/10 text-primary dark:text-primary-light"
      }
    : file.source === "kb"
      ? {
          label: t("ask_os.label_library_tag"),
          cls: "bg-gray-100 dark:bg-dark-1 text-gray-500 dark:text-gray-400"
        }
      : {
          label: t("ask_os.label_my_files_tag"),
          cls: "bg-gray-100 dark:bg-dark-1 text-gray-500 dark:text-gray-400"
        }

  // Lead icon: a progress RING (with %) while uploading/processing, a red
  // alert when failed, and the coloured file-type square when complete —
  // restoring the %-based progress the pre-card chip showed.
  const renderLead = () => {
    if (file.phase === "UPLOADING" || file.phase === "PROCESSING") {
      return (
        <span className="w-[34px] h-[34px] shrink-0 inline-flex items-center justify-center rounded-lg border border-light-border dark:border-border bg-genesis dark:bg-dark-2">
          <CircleProgress
            percentage={
              file.phase === "UPLOADING" ? file.uploadPct : file.processingPct
            }
            size={22}
            phase={file.phase}
          />
        </span>
      )
    }
    if (failed) {
      return (
        <span className="w-[34px] h-[34px] shrink-0 inline-flex items-center justify-center rounded-lg border border-red-300 dark:border-red-500/50 bg-red-50 dark:bg-red-900/20">
          <CircleProgress percentage={0} size={22} phase="FAILED" />
        </span>
      )
    }
    return (
      <span
        className="w-[34px] h-[34px] shrink-0 rounded-lg inline-flex items-center justify-center text-white"
        style={{ backgroundColor: iconBg }}
      >
        <FileText size={16} />
      </span>
    )
  }

  const renderSubStatus = () => {
    if (file.phase === "UPLOADING") {
      return (
        <span className="font-medium text-primary dark:text-primary-light">
          {t("rfp_os.label_file_status_uploading")}
        </span>
      )
    }
    if (file.phase === "PROCESSING") {
      return (
        <span className="font-medium text-amber-600 dark:text-amber-400">
          {file.transcription_status === "pending"
            ? "Transcribing…"
            : t("rfp_os.label_file_status_processing")}
        </span>
      )
    }
    if (failed) {
      return (
        <span className="font-medium text-red-500 dark:text-red-400">
          {t("rfp_os.label_file_status_failed")}
        </span>
      )
    }
    return <span>{ext.toUpperCase()}</span>
  }

  return (
    <span
      className={
        "inline-flex items-center gap-2.5 w-[248px] py-2 pl-2.5 pr-2 rounded-[10px] border shadow-sm " +
        (failed
          ? "border-red-300 dark:border-red-500/50 bg-red-50 dark:bg-red-900/20"
          : "border-light-border dark:border-border bg-white dark:bg-dark-3")
      }
      title={failed && file.error ? file.error : file.name}
    >
      {renderLead()}
      <span className="flex flex-col min-w-0 flex-1 gap-0.5">
        <span className="text-[13.5px] font-semibold leading-tight text-dark-2 dark:text-dark-6 truncate">
          {file.name}
        </span>
        <span className="inline-flex items-center gap-1.5 min-w-0 text-[11px] font-medium text-gray-400">
          <span
            className={`shrink-0 px-[7px] py-px rounded-full text-[10px] font-bold tracking-wide uppercase ${tag.cls}`}
          >
            {tag.label}
          </span>
          <span className="truncate">{renderSubStatus()}</span>
        </span>
      </span>
      {failed &&
        (file.s3Path || file.rawFile || file.pickFileId) &&
        onRetry && (
          <button
            type="button"
            className="p-1 shrink-0 rounded-full text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50"
            onClick={() => onRetry(file.localId)}
            disabled={disabled}
            aria-label={t("rfp_os.label_retry_file_aria", { name: file.name })}
            title={t("rfp_os.label_retry")}
          >
            <RotateCcw size={13} />
          </button>
        )}
      <button
        type="button"
        className="p-1 shrink-0 rounded-full text-dark-2 dark:text-dark-6 hover:bg-[whitesmoke] dark:hover:bg-dark-1 disabled:opacity-50"
        onClick={() => onRemove(file.localId)}
        disabled={disabled}
        aria-label={t("rfp_os.label_remove_file_aria", { name: file.name })}
        title={t("rfp_os.label_remove")}
      >
        <X size={14} />
      </button>
    </span>
  )
}
